import { Checkbox, Table, TableCell, TableRow } from '@amzn/meridian';
import { type LdapPermissionsTablePermissionFragment, TrafficProjectPermissionType } from 'generated/graphql';
import { type FC, useCallback, useState } from 'react';
import { setAllKeys } from '../TPShareModalUtil';
import { LDAPGroupSelect } from './LDAPGroupSelect/LDAPGroupSelect';
import { LDAPPermissionsTableActionBar } from './LDAPPermissionsTableActionBar';
import { LDAPPermissionsTableRow } from './LDAPPermissionsTableRow/LDAPPermissionsTableRow';

export type LDAPPermissionsTableProps = {
  selectedGroups: LdapPermissionsTablePermissionFragment[];
  setSelectedGroups: (permissionsSet: LdapPermissionsTablePermissionFragment[]) => void;
};

export const LDAPPermissionsTable: FC<LDAPPermissionsTableProps> = ({ selectedGroups, setSelectedGroups }) => {
  const [selectedRows, setSelectedRows] = useState<{
    [ldapGroup: LdapPermissionsTablePermissionFragment['ldapGroup']]: boolean;
  }>(
    selectedGroups.reduce(
      (selected, permissionOb) => {
        selected[permissionOb.ldapGroup] = false;
        return selected;
      },
      {} as {
        [ldapGroup: LdapPermissionsTablePermissionFragment['ldapGroup']]: boolean;
      },
    ),
  );

  const numRows = Object.keys(selectedRows).length;
  const checkedCount = Object.values(selectedRows).filter(Boolean).length;
  const allSelected = numRows === checkedCount;

  const onChangeHeader = () => {
    const allValues = !allSelected;
    const nextSelected = {
      ...setAllKeys(selectedRows, allValues),
    };
    setSelectedRows(nextSelected);
  };

  const onRowSelected = useCallback(
    (ldapGroup: string) => (value: boolean) =>
      setSelectedRows({
        ...selectedRows,
        [ldapGroup]: value,
      }),
    [
      selectedRows,
    ],
  );

  const onCloseActionBar = useCallback(() => {
    const nextSelected = {
      ...setAllKeys(selectedRows, false),
    };
    setSelectedRows(nextSelected);
  }, [
    selectedRows,
  ]);

  const addLDAPGroup = useCallback(
    (groupName: string) => {
      if (selectedGroups.find((group) => group.ldapGroup === groupName)) return;

      setSelectedGroups([
        ...selectedGroups,
        {
          action: 'edit',
          ldapGroup: groupName,
          permissionType: TrafficProjectPermissionType.TrafficProjectLdapPermission,
        },
      ]);

      onRowSelected(groupName)(false);
    },
    [
      onRowSelected,
      selectedGroups,
      setSelectedGroups,
    ],
  );

  const removeLDAPGroups = useCallback(
    (ldapGroups: string[]) => {
      setSelectedGroups(selectedGroups.filter((group) => !ldapGroups.includes(group.ldapGroup)));

      // Update checkbox selection
      setSelectedRows((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => !ldapGroups.includes(key))));
    },
    [
      selectedGroups,
      setSelectedGroups,
    ],
  );

  const getSelectedLDAPGroups = useCallback(
    () =>
      Object.entries(selectedRows)
        .filter(([, value]) => value === true)
        .map(([key]) => key),
    [
      selectedRows,
    ],
  );

  return (
    <Table
      headerRows={1}
      rowComponents={[
        TableRow,
        LDAPPermissionsTableRow,
      ]}
      showDividers
      spacing="small"
    >
      <LDAPPermissionsTableActionBar
        checkedCount={checkedCount}
        onCloseActionBar={onCloseActionBar}
        onRemoveBulkAction={() => removeLDAPGroups(getSelectedLDAPGroups())}
      >
        <LDAPGroupSelect onGroupSelected={addLDAPGroup} selectedGroups={selectedGroups} />
      </LDAPPermissionsTableActionBar>
      <TableRow>
        {/* We don't extract the header to it own component because Meridian 7 loses the bold styling of column text */}
        <TableCell>
          <Checkbox checked={allSelected} disabled={numRows === 0} onChange={onChangeHeader} />
        </TableCell>
        <TableCell>Name</TableCell>
        <TableCell>Permission</TableCell>
        <TableCell />
      </TableRow>
      {selectedGroups.map((group) => (
        <LDAPPermissionsTableRow
          key={group.ldapGroup}
          ldapData={group}
          onChangeRow={onRowSelected(group.ldapGroup)}
          onRemove={() =>
            removeLDAPGroups([
              group.ldapGroup,
            ])
          }
          selected={(ldapGroup: string) => selectedRows[ldapGroup]}
        />
      ))}
    </Table>
  );
};
